import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';
import { Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SfhHomes = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/sfh-homes`);
        if (!cancelled) setItems(r.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="sfh-homes-page">
      <SEO
        title={isZh ? '独栋住宅 | ALIGCUS 阿里建筑' : 'Single-Family Homes | ALIGCUS Construction'}
        description={isZh ? 'ALIGCUS 独栋住宅设计与建造作品集 — 加州优质单户住宅项目展示。' : 'ALIGCUS single-family home designs and builds — premier custom homes across California.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/sf-homes' : 'https://www.aligcus.com/sf-homes'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/sf-homes' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/sf-homes' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '独栋住宅' : 'Single-Family Homes', url: isZh ? 'https://www.aligcus.com/zh/sf-homes' : 'https://www.aligcus.com/sf-homes' },
          ]),
        ]}
      />

      {/* Banner */}
      <section className="relative flex items-center justify-center bg-[#7c1d1d]" style={{ minHeight: '260px' }}>
        <div className="text-center px-4 sm:px-6 py-12">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-3 leading-tight">
            {isZh ? '独栋住宅' : 'Single-Family Homes'}
          </h1>
          <p className="text-sm sm:text-xl text-white/80 font-light">
            {isZh ? '精选独栋住宅设计与建造作品' : 'A curated portfolio of single-family home designs'}
          </p>
        </div>
      </section>

      <main className="flex-grow py-12">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-16 text-gray-400">{isZh ? '加载中...' : 'Loading...'}</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-gray-400" data-testid="sfh-empty">
              {isZh ? '暂无内容，敬请期待' : 'Coming soon — content will be added shortly.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8" data-testid="sfh-grid">
              {items.map(item => (
                <div key={item.id} className="group" data-testid={`sfh-card-${item.id}`}>
                  <div className="aspect-[4/3] w-full overflow-hidden rounded-xl bg-gray-100 shadow-md group-hover:shadow-xl transition-all">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={(isZh ? item.text_zh : item.text_en) || 'Single-Family Home'}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <ImageIcon className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <p className="mt-4 text-base sm:text-lg text-gray-800 leading-relaxed whitespace-pre-line">
                    {(isZh ? item.text_zh : item.text_en) || ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SfhHomes;
